/**
 * The supplements the menu prescribes, and which of them the trainee actually took.
 *
 * The card existed before this and was dead: the coach endpoint always sent an empty list, so it
 * always rendered its "none prescribed" branch. `log.supplementsTaken` is the half that was missing —
 * per-supplement ticks, which the trainee's app has been writing all along.
 *
 * Untaken rows are dimmed rather than dropped, the same rule the food rows follow: a prescribed
 * supplement nobody took is the fact a trainer wants, and hiding it would report a perfect day.
 *
 * Supplements are deliberately absent from every total on this screen — they carry no macros in the
 * model, and a trainer reading "0 קל׳" beside a scoop of protein would rightly distrust the number.
 * The footnote says so rather than leaving it to be inferred.
 */

import Box from "@mui/material/Box";
import { v2 } from "../ui/v2Tokens";
import { POINTS } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import type { SupplementV2 } from "../model/nutritionV2Model";
import { EatenMark } from "./EatenMark";

const nums = { unicodeBidi: "isolate", direction: "ltr" as const, display: "inline-block" };

export const DaySupplementsCard = ({
  supplements,
  taken,
}: {
  supplements: SupplementV2[];
  taken: Record<string, boolean>;
}) => {
  const mode = useNutritionDisplayMode();
  if (!supplements.length) return null;
  const n = supplements.filter((sup) => taken[sup.uid] === true).length;

  return (
    <Box
      data-testid="log-supplements"
      sx={{
        backgroundColor: v2.surface,
        border: `1px solid ${v2.line}`,
        borderRadius: "14px",
        p: 1.5,
        mb: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ fontSize: 13.5, fontWeight: 700, color: v2.ink }}>תוספי תזונה</Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: 12, color: v2.ink3 }}>
          <Box component="span" sx={nums}>
            {n}
          </Box>{" "}
          מתוך{" "}
          <Box component="span" sx={nums}>
            {supplements.length}
          </Box>{" "}
          סומנו
        </Box>
      </Box>

      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {supplements.map((sup) => {
          const wasTaken = taken[sup.uid] === true;
          return (
            <Box
              key={sup.uid}
              data-testid="log-supplement-row"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: "8px",
                backgroundColor: v2.canvas,
                opacity: wasTaken ? 1 : 0.5,
              }}
            >
              <EatenMark state={wasTaken ? "all" : "none"} size={16} />
              <Box sx={{ fontSize: 12.5, fontWeight: 600, color: v2.ink }}>{sup.name}</Box>
              {sup.dosage ? <Box sx={{ fontSize: 11.5, color: v2.ink3 }}>{sup.dosage}</Box> : null}
              <Box sx={{ flex: 1 }} />
              {sup.timing ? <Box sx={{ fontSize: 11, color: v2.ink4 }}>{sup.timing}</Box> : null}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 1, fontSize: 11, color: v2.ink4 }}>
        {mode === POINTS
          ? "תוספים אינם נספרים בנקודות או במאקרו היומי."
          : "תוספים אינם נספרים בקלוריות או במאקרו היומי."}
      </Box>
    </Box>
  );
};
